// Design prototype data: replace with the catalogs the user can reach via MDS
// once the API exposes them. Names and generated logos come from the parallel
// picker prototype (disco-team/disco-apps#5577); the ids don't exist on the
// backend.

export type PickerCatalog = {
  id: string
  name: string
  logoUrl: string
  description: string
  trackCount: number
  url: string
}

export type CatalogGroup = {
  id: string
  name: string
  catalogIds: string[]
  /** Provided by DISCO; the user can pin it but not edit or delete it. */
  isDefault?: boolean
  /** Mirrors the user's Discover Music favourites. */
  isFavourites?: boolean
  /** Shown in the Source filter menu for one-click selection. */
  isPinned?: boolean
}

const NAMES = [
  'Warner Chappell Production Music',
  'Extreme Music',
  'APM Music',
  'Universal Production Music',
  'BMG Production Music',
  'Audio Network',
  'Musicbed',
  'Artlist',
  'Epidemic Sound',
  'Sony Music Publishing',
  'Kobalt Music',
  'Concord Music Publishing',
  'Downtown Music Publishing',
  'Big Machine Label Group',
  'Secretly Group',
  'Sub Pop Records',
  'Domino Recording Company',
  'Beggars Group',
  'Warp Records',
  'Ninja Tune',
  'Merge Records',
  'Matador Records',
  'Rough Trade Records',
  'XL Recordings',
  '4AD',
  'Partisan Records',
  'Dead Oceans',
  'Jagjaguwar',
  'Ghostly International',
  'Stones Throw Records',
  'Epitaph Records',
  'Fueled By Ramen',
  'Nettwerk Music Group',
  'Believe',
  'Empire Distribution',
  'CD Baby',
  'DistroKid Artists',
  'Position Music',
  'Songtradr',
  'Marmoset',
  'Musicbox',
  'Heavy Hitters Music',
  'Elias Music Library',
  'Associated Production Music',
  'FirstCom Music',
  'Killer Tracks',
  'Bleeding Fingers Music',
  'Big Sync Music',
  'West One Music Group',
  'Cavendish Music',
  'Atom Music Audio',
  'Brand X Music',
  'Two Steps From Hell',
  'Immediate Music',
  'Audiomachine',
  'Hans Zimmer Productions',
  'Mute Records',
  'Sacred Bones Records',
  'Big Dada',
  'Anti- Records',
  'Polyvinyl Record Co.',
  'Saddle Creek',
  'Thirty Tigers',
  'New West Records',
  'Rhino Entertainment',
  'Nonesuch Records',
  'Blue Note Records',
  'Verve Records',
  'Impulse! Records',
  'Deutsche Grammophon',
  'Hyperion Records',
  'Naxos Music Library',
  'Mad Decent',
  'Monstercat',
  'Anjunabeats',
  'Defected Records',
  'Spinnin’ Records',
  'Ultra Music',
  'Fool’s Gold Records',
  'Top Dawg Entertainment',
  'Quality Control Music',
  'Mass Appeal Records',
  'Rhymesayers Entertainment',
  'Dualtone Records',
  'Rounder Records',
  'Light in the Attic',
  'Numero Group',
  'Daptone Records',
  'Colemine Records',
  'Big Crown Records',
]

const LOGO_COLORS = [
  ['#1f2937', '#f59e0b'],
  ['#7c3aed', '#f5f3ff'],
  ['#0f766e', '#ccfbf1'],
  ['#b91c1c', '#fee2e2'],
  ['#1d4ed8', '#dbeafe'],
  ['#111827', '#f9fafb'],
  ['#c2410c', '#ffedd5'],
  ['#be185d', '#fce7f3'],
  ['#4d7c0f', '#ecfccb'],
  ['#0e7490', '#cffafe'],
]

// Simple marks drawn on a 40x40 tile; none use letters so they read as logos.
const LOGO_MARKS = [
  (fg: string) => `<circle cx="20" cy="20" r="11" fill="${fg}"/>`,
  (fg: string) =>
    `<circle cx="20" cy="20" r="11" fill="none" stroke="${fg}" stroke-width="4"/>`,
  (fg: string) => `<path d="M20 8 L32 31 H8 Z" fill="${fg}"/>`,
  (fg: string) =>
    [8, 14, 20, 26, 32]
      .map(
        (x, i) =>
          `<rect x="${x - 2}" y="${
            20 - [5, 10, 14, 8, 4][i]
          }" width="4" height="${
            [5, 10, 14, 8, 4][i] * 2
          }" rx="2" fill="${fg}"/>`,
      )
      .join(''),
  (fg: string) =>
    `<rect x="10" y="10" width="20" height="20" rx="3" fill="none" stroke="${fg}" stroke-width="4" transform="rotate(45 20 20)"/>`,
  (fg: string) =>
    `<path d="M6 22 Q13 8 20 20 T34 18" fill="none" stroke="${fg}" stroke-width="4" stroke-linecap="round"/>`,
  (fg: string) =>
    `<circle cx="14" cy="20" r="7" fill="${fg}"/><circle cx="26" cy="20" r="7" fill="none" stroke="${fg}" stroke-width="3"/>`,
  (fg: string) =>
    `<rect x="9" y="9" width="9" height="9" fill="${fg}"/><rect x="22" y="22" width="9" height="9" fill="${fg}"/><rect x="22" y="9" width="9" height="9" fill="none" stroke="${fg}" stroke-width="2"/>`,
]

/** Inline SVG so the demo needs no network and no real brand artwork. */
const fakeLogoUrl = (index: number): string => {
  const [bg, fg] = LOGO_COLORS[index % LOGO_COLORS.length]
  // Steps through marks at a different rate than colors so pairs rarely repeat.
  const mark = LOGO_MARKS[(index * 3) % LOGO_MARKS.length](fg)
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><rect width="40" height="40" fill="${bg}"/>${mark}</svg>`
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`
}

// Deterministic, so counts don't change between reloads of the demo.
const fakeTrackCount = (index: number): number =>
  ((index * 7919) % 48000) + 1200

// Default groups mirror Discover Music's catalog sets (from #5577); one
// catalog can be in several.
const DEFAULT_GROUP_MEMBERS: Record<string, string[]> = {
  Vintage: [
    'Blue Note Records',
    'Verve Records',
    'Impulse! Records',
    'Rhino Entertainment',
    'Light in the Attic',
    'Numero Group',
    'Daptone Records',
    'Colemine Records',
    'Big Crown Records',
    'Nonesuch Records',
    'Deutsche Grammophon',
    'Hyperion Records',
    'Rounder Records',
    'Associated Production Music',
    'Killer Tracks',
  ],
  Independent: [
    'Big Machine Label Group',
    'Secretly Group',
    'Sub Pop Records',
    'Domino Recording Company',
    'Beggars Group',
    'Warp Records',
    'Ninja Tune',
    'Merge Records',
    'Matador Records',
    'Rough Trade Records',
    'XL Recordings',
    '4AD',
    'Partisan Records',
    'Dead Oceans',
    'Jagjaguwar',
    'Ghostly International',
    'Stones Throw Records',
    'Epitaph Records',
    'Nettwerk Music Group',
    'Mute Records',
    'Sacred Bones Records',
    'Big Dada',
    'Anti- Records',
    'Polyvinyl Record Co.',
    'Saddle Creek',
    'Thirty Tigers',
    'New West Records',
    'Dualtone Records',
    'Rounder Records',
    'Light in the Attic',
    'Numero Group',
    'Daptone Records',
    'Colemine Records',
    'Big Crown Records',
    'Mad Decent',
    'Monstercat',
    'Anjunabeats',
    'Defected Records',
    'Fool’s Gold Records',
    'Rhymesayers Entertainment',
    'Mass Appeal Records',
    'Top Dawg Entertainment',
  ],
  Worldwide: [
    'Audio Network',
    'West One Music Group',
    'Cavendish Music',
    'Epidemic Sound',
    'Artlist',
    'BMG Production Music',
    'Kobalt Music',
    'Domino Recording Company',
    'Beggars Group',
    'Warp Records',
    'Ninja Tune',
    'Rough Trade Records',
    'XL Recordings',
    '4AD',
    'Big Dada',
    'Mute Records',
    'Defected Records',
    'Spinnin’ Records',
    'Anjunabeats',
    'Monstercat',
    'Nettwerk Music Group',
    'Believe',
    'Deutsche Grammophon',
    'Hyperion Records',
    'Naxos Music Library',
  ],
  'Trailer Music': [
    'Bleeding Fingers Music',
    'Atom Music Audio',
    'Brand X Music',
    'Two Steps From Hell',
    'Immediate Music',
    'Audiomachine',
    'Hans Zimmer Productions',
    'Position Music',
    'Extreme Music',
  ],
  'One-stop': [
    'Musicbed',
    'Artlist',
    'Epidemic Sound',
    'Position Music',
    'Songtradr',
    'Marmoset',
    'Musicbox',
    'Big Sync Music',
    'Heavy Hitters Music',
    'Secretly Group',
    'Domino Recording Company',
    'Ninja Tune',
    'Merge Records',
    'Sub Pop Records',
    'Nettwerk Music Group',
    'Thirty Tigers',
  ],
  'Production Music': [
    'Warner Chappell Production Music',
    'Extreme Music',
    'APM Music',
    'Universal Production Music',
    'BMG Production Music',
    'Audio Network',
    'West One Music Group',
    'Cavendish Music',
    'FirstCom Music',
    'Killer Tracks',
    'Associated Production Music',
    'Elias Music Library',
    'Naxos Music Library',
    'Big Sync Music',
    'Heavy Hitters Music',
  ],
}

// One line per group, so each catalog gets a description for its hover card.
const GROUP_BLURBS: Record<string, string> = {
  Vintage: 'Classic recordings and reissues with an authentic period sound.',
  Independent: 'Artist-driven releases from an independent label.',
  Worldwide: 'Music from outside the US, cleared for global use.',
  'Trailer Music': 'Big, cinematic cues built for trailers and promos.',
  'One-stop': 'Master and publishing cleared together for fast licensing.',
  'Production Music': 'Library music written and cleared for picture.',
}

const describe = (name: string): string =>
  [
    `${name} on DISCO.`,
    ...Object.entries(DEFAULT_GROUP_MEMBERS)
      .filter(([, members]) => members.includes(name))
      .map(([group]) => GROUP_BLURBS[group]),
  ].join(' ')

const slug = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')

export const MOCK_CATALOGS: PickerCatalog[] = NAMES.map((name, index) => ({
  id: `c${index}`,
  name,
  logoUrl: fakeLogoUrl(index),
  description: describe(name),
  trackCount: fakeTrackCount(index),
  // Follows the public catalog URL shape (<business>.disco.ac); these
  // businesses don't exist.
  url: `https://${slug(name)}.disco.ac`,
}))

// The user's favourites in Discover Music (from #5577's demo data).
const DISCOVER_FAVOURITES = [
  'Warner Chappell Production Music',
  'Extreme Music',
  'Musicbed',
  'Position Music',
  'Big Sync Music',
  'Secretly Group',
]

const idsFor = (members: string[]) =>
  MOCK_CATALOGS.filter(c => members.includes(c.name)).map(c => c.id)

export const MOCK_GROUPS: CatalogGroup[] = [
  {
    id: 'g-favourites',
    name: 'Favourites',
    catalogIds: idsFor(DISCOVER_FAVOURITES),
    isDefault: true,
    isFavourites: true,
    isPinned: true,
  },
  ...Object.entries(DEFAULT_GROUP_MEMBERS).map(([name, members]) => ({
    id: `g-${slug(name)}`,
    name,
    catalogIds: idsFor(members),
    isDefault: true,
  })),
]
